/***************************************************************************************************
** useForm
***************************************************************************************************/

import { useRef, useMemo, useState, useCallback, useEffect } from "react";
import { toastify } from "@vendors";
import useFormValidation from "@hooks/useFormValidation";
import { Router, buildUrl } from "@utils";

const BLANK_VALUES = [undefined, null, ""];
const isValueBlank = (value) => (BLANK_VALUES.includes(value));
const hasValueChanged = (value, initialValue) => (!(isValueBlank(value) && isValueBlank(initialValue)) && (value !== initialValue));

const LOADED = "loaded";
const SUBMITTED = "submitted";
const SUBMITTING = "submitting";

// const getUpdatedFormValues = (initialValues, currentValues, options = {}) => {
//   const ignore = options.ignore || [];
//   const updatedValues = {};

//   Object.entries(currentValues).forEach(([key, currentValue]) => {
//     const initialValue = initialValues[key];

//     let shouldAdd = false;

//     if (ignore.includes(key)) {
//       shouldAdd = true;
//     } else if (!Object.keys(initialValues).includes(key)) {
//       shouldAdd = true;
//     } else if (isValueBlank(currentValue) && isValueBlank(initialValue)) {
//       shouldAdd = false;
//     } else if (JSON.stringify(currentValue) !== JSON.stringify(initialValue)) {
//       shouldAdd = true;
//     }

//     if (shouldAdd) {
//       updatedValues[key] = currentValue;
//     }
//   });

//   return updatedValues;
// };

const defaultProps = {
  requestMethod: "PATCH",
  fieldValidations: {},
  isDisabled: false,

  initialFormValues: {},
  additionalFormValues: {},

  onValidateSuccessMessage: false,
  onValidateFailureMessage: "There is an issue with fields!",
  onSubmitSuccessMessage:   "Record has been updated!",
  onSubmitFailureMessage:   "Something went wrong!",
};

export default function useForm(props) {
  const {
    path,
    requestMethod,
    resource,
    params,
    fieldValidations,
    isDisabled,

    onValidateSuccess,
    onValidateFailure,
    onSubmitSuccess,
    onSubmitFailure,

    initialFormValues,
    setInitialFormValues,
    // additionalFormValues,

    onValidateSuccessMessage,
    onValidateFailureMessage,
    onSubmitSuccessMessage,
    onSubmitFailureMessage,
  } = { ...defaultProps, ...props };

  const formRef = useRef(null);

  const requestUrl = useMemo(() => Router.buildUrlFor(`/api/${path}`, params), [path, params]);

  const onValidateSuccessFallback = ((_data) => toastify.success(onValidateSuccessMessage));
  const onValidateFailureFallback = ((error) => toastify.error(error?.message || onValidateFailureMessage));
  const onSubmitSuccessFallback   = ((_data) => toastify.success(onSubmitSuccessMessage));
  const onSubmitFailureFallback = ((error) => toastify.error(error?.message || onSubmitFailureMessage));

  const handleValidateSuccess = onValidateSuccess || onValidateSuccessFallback;
  const handleValidateFailure = onValidateFailure || onValidateFailureFallback;
  const handleSubmitSuccess   = onSubmitSuccess || onSubmitSuccessFallback;
  const handleSubmitFailure   = onSubmitFailure || onSubmitFailureFallback;

  /***************************************************************************************************
  ** Form Values
  ***************************************************************************************************/

  const [formValues, setFormValues] = useState(initialFormValues);
  const setFormValue = useCallback((name, value) => setFormValues((values) => ({ ...values, [name]: value })), []);
  const updateFormValues = useCallback((data) => setFormValues((values) => ({ ...values, ...data })), []);
  const resetFormValues = useCallback(() => setFormValues(initialFormValues), [initialFormValues]);

  /***************************************************************************************************
  ** Form State
  ***************************************************************************************************/

  const [formStateValue, setFormStateValue] = useState(LOADED);

  const formState = useMemo(() => ({
    value: formStateValue,
    isLoaded:     (formStateValue === LOADED),
    isSubmitted:  (formStateValue === SUBMITTED),
    isSubmitting: (formStateValue === SUBMITTING),
  }), [formStateValue]);

  /***************************************************************************************************
  ** Form Response
  ***************************************************************************************************/

  const [formResponse, setFormResponse] = useState(null);

  /***************************************************************************************************
  ** CHANGES (WIP)
  ***************************************************************************************************/

  const [formChanges, setFormChanges] = useState([]);
  const [hasFormChanges, setHasFormChanges] = useState(false);

  useEffect(() => {
    const changes = [];
    let hasFormChanges = false;

    Object.entries(initialFormValues).forEach(([key, initialValue]) => {
      const value = formValues[key];

      if (hasValueChanged(value, initialValue)) {
        changes.push(key);
        hasFormChanges = true;
      }
    });

    setFormChanges(changes);
    setHasFormChanges(hasFormChanges);
  }, [formValues, initialFormValues]);

  /***************************************************************************************************
  ** ERRORS
  ***************************************************************************************************/

  const [formErrors, setFormErrors] = useState({});
  const clearFormError = useCallback((name) => {
    setFormErrors((currentErrors) => {
      const updatedErrors = { ...currentErrors };
      delete updatedErrors[name];
      return updatedErrors;
    });
  }, []);

  /***************************************************************************************************
  ** VALIDATIONS
  ***************************************************************************************************/

  const { validate } = useFormValidation({
    fieldValidations: fieldValidations,
    setFormErrors: setFormErrors,
  });

  /***************************************************************************************************
  ** REQUEST
  ***************************************************************************************************/

  const sendFormSubmitRequest = useCallback(async (data) => {
    const url = requestUrl && buildUrl(requestUrl, params);

    const response = await fetch(url, {
      method: requestMethod,
      headers: new Headers({
        "Accept": "application/json",
        "Cache-Control": "no-cache, no-store",
        "Authorization-Token": window.appContext.authorizationToken,
      }),
      body: data,
    });

    const responseBody = await response.json().catch(() => ({ error: response.statusText }));

    if (response.ok) {
      return responseBody;
    } else {
      throw new Error(responseBody.error || responseBody.status);
    }
  }, [requestUrl, params, requestMethod]);

  /***************************************************************************************************
  ** HANDLERS
  ***************************************************************************************************/

  const handleFormFieldChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormValue(name, value);
    clearFormError(name);
  }, [setFormValue, clearFormError]);

  const handleFormSubmit = useCallback((event) => {
    event?.preventDefault();
    if (isDisabled) return;

    // TODO: Only submit changed values
    // const updatedValues = getUpdatedFormValues(initialFormValues, formValues, { ignore: Object.keys(additionalFormValues) });
    // if (isObjectEmpty(updatedValues)) return;

    setFormStateValue(SUBMITTING);

    const isFormValid = validate(formValues);

    if (!isFormValid) {
      setFormStateValue(LOADED);
      handleValidateFailure();
    } else {
      const submitData = new FormData();

      Object.entries(formValues).forEach(([field, value]) => {
        const key = `${resource}[${field}]`;

        if (isValueBlank(value)) {
          submitData.append(key, "");
        } else if (value instanceof File) {
          submitData.append(key, value, value.name);
        } else if (typeof value === "object") {
          submitData.append(key, JSON.stringify(value));
        } else {
          submitData.append(key, value);
        }
      });

      handleValidateSuccess();

      sendFormSubmitRequest(submitData).then((data) => {
        setFormResponse(data.records || data);
        setFormStateValue(SUBMITTED);
        updateFormValues(data.records);

        if (setInitialFormValues) {
          setInitialFormValues(data.records);
        }

        handleSubmitSuccess(data.records);
      }).catch((error) => {
        setFormStateValue(LOADED);
        handleSubmitFailure(error);
        console.error(error);
      });
    }
  }, [
    resource,
    // initialFormValues,
    setInitialFormValues,
    // additionalFormValues,
    formValues,
    updateFormValues,
    validate,
    sendFormSubmitRequest,

    handleValidateSuccess,
    handleValidateFailure,
    handleSubmitSuccess,
    handleSubmitFailure,
    isDisabled,
  ]);

  const handleFormReset = useCallback(() => {
    setFormStateValue(LOADED);
    resetFormValues();
  }, [resetFormValues]);

  /***************************************************************************************************
  ** RETURN
  ***************************************************************************************************/

  return {
    formRef,
    formState,
    formValues,
    formErrors,
    formResponse,
    formChanges, // WIP
    hasFormChanges, // WIP
    handleFormFieldChange,
    handleFormSubmit,
    handleFormReset,
    setFormValues,
  };
}
